// Article registry. Each article module exports its own body component while
// the metadata now lives in `meta.ts` so the marketing Worker can inject the
// same route titles/descriptions server-side for social crawlers.
import type { ComponentType } from "react";
import type { ArticleMeta } from "./meta";
import * as howManyTrainingZones from "./how-many-training-zones";
import * as fixedOrAdaptivePlan from "./fixed-or-adaptive-plan";
import * as is8020Best from "./is-80-20-best";
import * as norwegianMethod from "./norwegian-method";
import * as eliteDistanceRunners from "./elite-distance-runners";
import * as criticalSpeedWithoutALab from "./critical-speed-without-a-lab";
import * as whyRunningInjuriesHappen from "./why-running-injuries-happen";
import * as strengthTrainingForRunners from "./strength-training-for-runners";
import * as carbohydrateDuringAMarathon from "./carbohydrate-during-a-marathon";
import * as carbonPlatedShoes from "./carbon-plated-shoes";
import * as runningInTheHeat from "./running-in-the-heat";
import * as menstrualCycleAndRunning from "./menstrual-cycle-and-running";
import * as runningCadence from "./running-cadence";
import * as predictMarathonTime from "./predict-marathon-time";
import * as howLittleTrainingKeepsFitness from "./how-little-training-keeps-fitness";
import * as sleepAndRunning from "./sleep-and-running";
import * as recoveryMethods from "./recovery-methods";
import * as stretchingBeforeRunning from "./stretching-before-running";
import * as caffeineAndRunning from "./caffeine-and-running";
import * as howMuchToDrink from "./how-much-to-drink";
import * as treadmillOrOutside from "./treadmill-or-outside";
import * as runningAsYouGetOlder from "./running-as-you-get-older";
import * as eatingEnoughToTrain from "./eating-enough-to-train";
import * as the10PercentRule from "./the-10-percent-rule";
import * as mentalFatigue from "./mental-fatigue";
import * as overreachingAndOvertraining from "./overreaching-and-overtraining";
import * as wristHeartRate from "./wrist-heart-rate";
import * as intervalsOrSteadyRunning from "./intervals-or-steady-running";
import * as hillIntervals from "./hill-intervals";
import * as musicAndRunning from "./music-and-running";
import * as holdingUpLateInARun from "./holding-up-late-in-a-run";
import * as watchVo2max from "./watch-vo2max";
import * as intervalLength from "./interval-length";
import * as howHardDidItFeel from "./how-hard-did-it-feel";
import * as comingBackAfterABreak from "./coming-back-after-a-break";
import * as marathonPacing from "./marathon-pacing";
import * as restBetweenIntervals from "./rest-between-intervals";
import * as longRunLength from "./long-run-length";
import * as trainByRecovery from "./train-by-recovery";
import * as marathonTaper from "./marathon-taper";
import * as heartRateDrift from "./heart-rate-drift";
import * as targetsAndRanges from "./targets-and-ranges";
import * as easyIntensity from "./easy-intensity";

export type ArticleModule = {
  meta: ArticleMeta;
  Body: ComponentType;
};

export const articles: ArticleModule[] = [
  howManyTrainingZones,
  fixedOrAdaptivePlan,
  is8020Best,
  norwegianMethod,
  eliteDistanceRunners,
  criticalSpeedWithoutALab,
  whyRunningInjuriesHappen,
  strengthTrainingForRunners,
  carbohydrateDuringAMarathon,
  carbonPlatedShoes,
  runningInTheHeat,
  menstrualCycleAndRunning,
  runningCadence,
  predictMarathonTime,
  howLittleTrainingKeepsFitness,
  sleepAndRunning,
  recoveryMethods,
  stretchingBeforeRunning,
  caffeineAndRunning,
  howMuchToDrink,
  treadmillOrOutside,
  runningAsYouGetOlder,
  eatingEnoughToTrain,
  the10PercentRule,
  mentalFatigue,
  overreachingAndOvertraining,
  wristHeartRate,
  intervalsOrSteadyRunning,
  hillIntervals,
  musicAndRunning,
  comingBackAfterABreak,
  marathonPacing,
  restBetweenIntervals,
  intervalLength,
  holdingUpLateInARun,
  watchVo2max,
  howHardDidItFeel,
  longRunLength,
  trainByRecovery,
  marathonTaper,
  heartRateDrift,
  targetsAndRanges,
  easyIntensity,
];

// Jørgen wants these first in the listing regardless of date.
const pinned = ["how-many-training-zones", "fixed-or-adaptive-plan"];
const pinRank = (a: ArticleModule) => {
  const i = pinned.indexOf(a.meta.slug);
  return i < 0 ? pinned.length : i;
};

// Pinned first, then newest → oldest; the sort is stable, so articles
// sharing a date keep the order above.
export const articlesByDate = [...articles].sort(
  (a, b) => pinRank(a) - pinRank(b) || b.meta.date.localeCompare(a.meta.date),
);

export function findArticle(slug: string): ArticleModule | undefined {
  return articles.find((a) => a.meta.slug === slug);
}
